import { Localized } from "@fluent/react";
import { CircularProgress, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthPageLayout } from "./AuthPageLayout";
import { LanguageSelector } from "./LanguageSelector";
import { useAppDispatch } from "./app/hooks";
import { showSuccess, showError } from "./features/notifications/notificationsSlice";
import { fetchSSOToken } from "./features/user/userSlice";

const getSsoErrorMessage = (errorCode: string | null) => {
    switch (errorCode) {
        case "missing_sso_claims":
            return "login-sso-microsoft-missing-claims";

        case "tenant_not_allowed":
            return "login-sso-microsoft-tenant-not-allowed";

        case "external_users_not_allowed":
            return "login-sso-microsoft-external-users-not-allowed";

        case "email_exists_in_different_company":
            return "login-sso-microsoft-email-exists-in-different-company";

        case "sso_login_already_linked":
            return "login-sso-microsoft-login-already-linked";

        case "sso_link_failed":
            return "login-sso-microsoft-link-failed";

        case "self_registration_disabled":
            return "login-sso-microsoft-self-registration-disabled";

        case "user_not_found":
            return "login-sso-microsoft-user-not-found";

        default:
            return "error-login-failed";
    }
};

const SsoCompleted = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const handleSsoRedirect = async () => {
            const id = searchParams.get("id");
            const error = searchParams.get("error");
            const registrationId = searchParams.get("registrationId");
            const returnUrl = searchParams.get("returnUrl") ?? "/";

            if (error) {
                dispatch(showError(getSsoErrorMessage(error)));
                navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
                return;
            }

            if (registrationId) {
                navigate(`/sso-register?registrationId=${registrationId}&returnUrl=${encodeURIComponent(returnUrl)}`, {
                    replace: true,
                });
                return;
            }

            if (!id) {
                dispatch(showError("login-sso-microsoft-missing-login-id"));
                navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
                return;
            }

            try {
                await dispatch(fetchSSOToken(id)).unwrap();

                dispatch(showSuccess("login-sso-microsoft-login-success"));
                navigate(returnUrl, { replace: true });
            } catch {
                dispatch(showError("error-login-failed"));
                navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
            }
        };

        handleSsoRedirect();
    }, [dispatch, navigate, searchParams]);

    return (
        <AuthPageLayout>
            <Grid container direction="column" spacing={3}>
                <Grid item>
                    <CircularProgress />
                </Grid>
                <Grid item>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                        <Localized id="sso-completed-loading">
                            <span>Signing you in...</span>
                        </Localized>
                    </Typography>
                </Grid>
                <Grid item textAlign="center">
                    <LanguageSelector />
                </Grid>
            </Grid>
        </AuthPageLayout>
    );
};

export default SsoCompleted;